import {
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'agm-marker',
  host: { '[attr.data-unique-id]': 'uniqueId' },
  template: `
    <ng-template #infoWindowTemplate>
      <div class="agm-info-window-content">
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
  standalone: false,
})
export class AgmMarkerComponent implements OnChanges {
  uniqueId = `obj-${Math.random().toString(36).substr(2, 9)}`; // Genera un ID unico
  @Input() latitude!: number;
  @Input() longitude!: number;
  @Input() enableInfoWindow: boolean = false; // Default: info window abilitata
  /**
   * Label accetta sia una stringa che un oggetto con classe personalizzata o può essere null.
   *  color: '#FFFFFF',
   *       fontFamily: '',
   *       fontSize: '12px',
   *       fontWeight: 'normal',
   */
  @Input() label?:
    | string
    | {
        text: string;
        className?: string;
        color?: string;
        fontFamily?: string;
        fontSize?: string;
        fontWeight?: string;
      }
    | null;
  /**
   * Icon URL accetta sia stringhe, oggetti personalizzati o può essere null.
   */
  @Input() iconUrl?:
    | string
    | { url: string; scaledSize?: { height: number; width: number } }
    | null;
  @Input() markerDraggable: boolean = false; // Gestione del valore di default
  @Input() clickable: boolean = false;

  /**
   * Aggiunto per supportare i dati aggiuntivi passati al marker.
   */
  @Input() data?: any;

  @Output() markerClick = new EventEmitter<any>();
  @Output() centerChange = new EventEmitter<{ lat: number; lng: number }>();
  @Output() dragEnd = new EventEmitter<{ coords: { lat: number; lng: number } }>();

  private marker: google.maps.Marker | null = null;
  private infoWindow!: google.maps.InfoWindow;

  @ViewChild('infoWindowTemplate', { static: true }) infoWindowTemplate!: TemplateRef<any>;
  @ViewChild('infoWindowTemplate', { static: true, read: ViewContainerRef })
  viewContainerRef!: ViewContainerRef;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markerDraggable'] && this.marker) {
      this.updateDraggableState(); // Quando markerDraggable cambia, aggiorna lo stato
    }
  }

  initialize(map: google.maps.Map) {
    const markerOptions: google.maps.MarkerOptions = {
      position: { lat: this.latitude, lng: this.longitude },
      map,
      label: this.parseLabel(this.label), // Configura la label dinamicamente
      draggable: this.markerDraggable,
      icon: this.iconUrl && this.parseIconUrl(this.iconUrl), // Configura l'icona dinamicamente
    };

    this.marker = new google.maps.Marker(markerOptions);

    // Configurazione dell'info window
    this.infoWindow = new google.maps.InfoWindow({
      content: this.renderDynamicContent(),
    });

    // Listener per il click sul marker
    if (this.clickable) {
      this.marker.addListener('click', (event: google.maps.MapMouseEvent) => {
        this.markerClick.emit({ event, uniqueId: this.uniqueId });
      });
    }

    this.updateDraggableState(); // Registra il listener dragend indipendentemente dallo stato iniziale
  }

  private updateDraggableState() {
    if (!this.marker) return;

    this.marker.setDraggable(this.markerDraggable);

    // Rimuove il listener precedente e lo riaggiunge per evitare duplicazioni
    google.maps.event.clearListeners(this.marker, 'dragend');

    if (this.markerDraggable) {
      this.marker.addListener('dragend', () => {
        const position = this.marker ? this.marker.getPosition() : null;
        if (position) {
          this.centerChange.emit({ lat: position.lat(), lng: position.lng() });
          this.dragEnd.emit({ coords: { lat: position.lat(), lng: position.lng() } });
        }
      });
    }
  }

  /**
   * Converte `iconUrl` in un formato accettabile per Google Maps.
   */
  private parseIconUrl(
    iconUrl?: string | { url: string; scaledSize?: { height: number; width: number } } | null
  ): any {
    if (!iconUrl) return undefined; // Nessuna icona

    if (typeof iconUrl === 'string') {
      // È una stringa: restituisci l'URL come icona
      return {
        url: this.resolveRelativeUrl(iconUrl),
      };
    } else if (iconUrl && typeof iconUrl === 'object') {
      // È un oggetto personalizzato
      return {
        url: this.resolveRelativeUrl(iconUrl.url),
        scaledSize: iconUrl.scaledSize
          ? new google.maps.Size(iconUrl.scaledSize.width, iconUrl.scaledSize.height)
          : null,
      };
    }
    return undefined; // Nessuna icona
  }

  /**
   * Converte URL relativi in URL assoluti.
   */
  private resolveRelativeUrl(url: string): string {
    if (url.startsWith('/')) {
      return `${window.location.origin}${url}`; // Prepend l'host se è un URL relativo
    }
    return url; // Ritorna URL assoluto
  }

  /**
   * Converte `label` in un formato accettabile per Google Maps.
   */
  private parseLabel(
    label?:
      | string
      | {
          text: string;
          className?: string;
          color?: string;
          fontFamily?: string;
          fontSize?: string;
          fontWeight?: string;
        }
      | null
  ): google.maps.MarkerLabel | undefined {
    if (!label) return undefined; // Nessuna label

    if (typeof label === 'string') {
      return { text: label }; // Restituisce solo il testo
    } else if (label && typeof label === 'object') {
      return {
        text: label.text,
        className: label.className, // Applica la classe CSS personalizzata
        color: label.color,
        fontFamily: label.fontFamily,
        fontSize: label.fontSize,
        fontWeight: label.fontWeight,
      };
    }
    return undefined; // Nessuna label
  }

  private renderDynamicContent(): HTMLElement {
    const element = document.createElement('div');
    const embeddedView = this.viewContainerRef.createEmbeddedView(this.infoWindowTemplate);

    // Rendi il contenuto visibile
    embeddedView.detectChanges();

    // Aggiungi il contenuto generato dinamicamente
    embeddedView.rootNodes.forEach((node) => element.appendChild(node));
    return element;
  }

  // Metodo per ottenere il google.maps.Marker
  getGoogleMarker(): google.maps.Marker {
    if (!this.marker) {
      throw new Error('Il marker non è stato inizializzato correttamente.');
    }
    return this.marker;
  }

  openInfoWindow(): void {
    if (this.marker) {
      this.infoWindow.open(this.marker.getMap(), this.marker);
    }
  }

  closeInfoWindow(): void {
    this.infoWindow.close();
  }

  remove(): void {
    if (this.marker) {
      this.marker.setMap(null);
      this.marker = null;
    }
  }
}
